import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./monthPicker.css";

import AdminLayout from "../../layout/admin";
import { getMethodApiCall } from "../../services/api";
import GenericDataLayout from "../../components/custom/GenericDataLayout";
import GenericTable from "../../components/custom/GenericTable";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const IndustryDashboard = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ traineeSummary: [], meta: {} });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const selectedYear = String(selectedDate.getFullYear());
  const selectedMonth = String(selectedDate.getMonth() + 1);
  const [eligibilityFilter, setEligibilityFilter] = useState("all");
  const [selectedIti, setSelectedIti] = useState("all");
  const [selectedTrade, setSelectedTrade] = useState("all");

  const user = JSON.parse(localStorage.getItem("loginedUser"));
  const industryId = user?.industry?.id;

  const fetchAttendance = useCallback(async () => {
    if (!industryId) return;
    setLoading(true);
    try {
      const url = `/v1/api/industry/attendance-by-industry/${industryId}?page=${page}&limit=${limit}&year=${selectedYear}&month=${selectedMonth}`;
      const res = await getMethodApiCall(url);
      if (res?.status === 200) {
        setData(res.data);
      }
    } catch (error) {
      console.error("Error fetching industry attendance:", error);
    } finally {
      setLoading(false);
    }
  }, [industryId, page, limit, selectedYear, selectedMonth]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  const handleClearFilters = () => {
    setPage(1);
    setLimit(10);
    setSelectedDate(new Date());
    setEligibilityFilter("all");
    setSelectedIti("all");
    setSelectedTrade("all");
  };

  const allTrainees = data?.traineeSummary || [];

  const itiOptions = (data?.filterOptions?.itis || []).map((iti) => ({
    label: iti.name,
    value: String(iti.id),
  }));

  const tradeOptions = (data?.filterOptions?.trades || []).map((trade) => ({
    label: trade.name,
    value: String(trade.id),
  }));

  const applySelectedFilterContext = (trainee) => {
    const result = { ...trainee };
    if (selectedTrade !== "all") {
      result.trade_id = parseInt(selectedTrade, 10);
      result.trade_name =
        tradeOptions.find((o) => o.value === selectedTrade)?.label || trainee.trade_name;
    }
    if (selectedIti !== "all") {
      result.iti_id = parseInt(selectedIti, 10);
      result.iti_name =
        itiOptions.find((o) => o.value === selectedIti)?.label || trainee.iti_name;
    }
    return result;
  };

  const filtersConfig = [
    {
      type: "custom",
      render: () => (
        <div className="w-full">
          <label className="text-xs font-semibold text-primary mb-1.5 block">Month & Year</label>
          <DatePicker
            selected={selectedDate}
            onChange={(date) => {
              setSelectedDate(date);
              setPage(1);
              setSelectedIti("all");
              setSelectedTrade("all");
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            maxDate={new Date()}
            onKeyDown={(e) => e.preventDefault()}
            wrapperClassName="w-full"
            className="w-full text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary/30 focus:border-primary outline-none transition-all bg-white hover:border-gray-300 h-[36px] px-3 py-1.5 text-gray-700 cursor-pointer caret-transparent"
          />
        </div>
      ),
    },
    {
      type: "select",
      label: "Eligibility",
      value: eligibilityFilter,
      options: [
        { label: "Eligible Students", value: "eligible" },
        { label: "Not Eligible Students", value: "not_eligible" },
      ],
      onChange: (val) => {
        setEligibilityFilter(val);
        setPage(1);
      },
    },
    {
      type: "select",
      label: "Trade",
      value: selectedTrade,
      options: tradeOptions,
      onChange: (val) => {
        setSelectedTrade(val);
        setPage(1);
      },
    },
    {
      type: "select",
      label: "ITI",
      value: selectedIti,
      options: itiOptions,
      onChange: (val) => {
        setSelectedIti(val);
        setPage(1);
      },
    },
  ];

  const filteredTrainees = allTrainees
    .filter((t) => {
      if (eligibilityFilter === "eligible" && t.attendancePercentage < 80) return false;
      if (eligibilityFilter === "not_eligible" && t.attendancePercentage >= 80) return false;
      if (
        selectedIti !== "all" &&
        !((t.iti_ids || []).map(String).includes(String(selectedIti)))
      ) return false;
      if (
        selectedTrade !== "all" &&
        !((t.trade_ids || []).map(String).includes(String(selectedTrade)))
      ) return false;
      return true;
    })
    .map(applySelectedFilterContext);

  const totalPages = Math.ceil(filteredTrainees.length / limit) || 1;
  const paginatedTrainees = filteredTrainees.slice((page - 1) * limit, page * limit);

  const paginationConfig = {
    page,
    limit,
    totalRecords: filteredTrainees.length,
    totalPages,
    onPageChange: (newPage) => setPage(newPage),
    onLimitChange: (newLimit) => { setLimit(newLimit); setPage(1); },
  };

  const workingDays = data?.meta?.totalWorkingDays || "-";
  const monthName = MONTH_NAMES[parseInt(selectedMonth) - 1] || "";

  const allFiltersSelected =
    selectedDate != null &&
    eligibilityFilter === "eligible" &&
    selectedTrade !== "all" &&
    selectedIti !== "all";

  const submittedDoc = (data?.submittedDocuments || []).find(
    (d) => String(d.iti_id) === selectedIti && String(d.trade_id) === selectedTrade
  );
  const hasSubmittedDocument = allFiltersSelected && !!submittedDoc?.s3_url;

  const columns = [
    {
      header: "Registration ID",
      render: (row) => row?.registration_id || "-",
    },
    {
      header: "Trainee Name",
      render: (row) => row?.name || "-",
    },
    {
      header: "ITI",
      className: "text-center",
      cellClassName: "text-center",
      render: (row) => row?.iti_name || "-",
    },
    {
      header: "Trade",
      className: "text-center",
      cellClassName: "text-center",
      render: (row) => row?.trade_name || "-",
    },
    {
      header: "Working Days",
      className: "text-center",
      cellClassName: "text-center",
      render: (row) => row?.totalWorkingDays ?? "-",
    },
    {
      header: "Days Present",
      className: "text-center",
      cellClassName: "text-center",
      render: (row) => row?.daysPresent ?? "-",
    },
    {
      header: "Attendance %",
      className: "text-center",
      cellClassName: "text-center",
      render: (row) => {
        const pct = row?.attendancePercentage;
        if (pct == null) return "-";
        const colorClass = pct >= 80 ? "text-green-600" : "text-red-500";
        return <span className={`font-semibold ${colorClass}`}>{pct}%</span>;
      },
    },
  ];

  return (
    <AdminLayout>
      <GenericDataLayout
        subtitle={user?.industry?.industry_name || ""}
        title="Welcome"
        filters={filtersConfig}
        onResetFilters={handleClearFilters}
        pagination={paginationConfig}
        isLoading={loading}
        actions={[]}
      >
        <GenericTable
          columns={columns}
          data={paginatedTrainees}
          pagination={paginationConfig}
        />

        {filteredTrainees.length > 0 && (
          <div className="flex justify-end p-4 border-t border-gray-100">
            <button
              disabled={!allFiltersSelected}
              onClick={() => {
                if (hasSubmittedDocument) {
                  window.open(submittedDoc.s3_url, "_blank", "noopener,noreferrer");
                  return;
                }
                navigate("/industry/approve", {
                  state: {
                    traineeSummary: filteredTrainees,
                    month: selectedMonth,
                    year: selectedYear,
                    monthName,
                    totalWorkingDays: data?.meta?.totalWorkingDays,
                    industryId,
                  },
                });
              }}
              className={`px-6 py-2.5 text-sm font-semibold rounded-lg shadow-sm transition-all ${
                allFiltersSelected
                  ? "text-white bg-primary hover:bg-primary/90 cursor-pointer"
                  : "text-gray-400 bg-gray-200 cursor-not-allowed"
              }`}
            >
              {hasSubmittedDocument ? "View Submitted Document" : "Approve"}
            </button>
          </div>
        )}
      </GenericDataLayout>
    </AdminLayout>
  );
};

export default IndustryDashboard;
